/**
 * Compteur de visites maison (migrations 0523, 0692) — PUR, testé, partagé
 * site ⇄ app.
 *
 * Rien de personnel : un chemin nettoyé (identifiants remplacés par `:id`, sauf
 * celui d'une page de contenu public : carte, pack, exercice…), un
 * identifiant de session aléatoire propre à l'ONGLET (`sessionStorage`) et un
 * identifiant de VISITEUR DU JOUR (`localStorage`, remplacé chaque jour : on
 * compte les visiteurs uniques d'une journée sans suivre quelqu'un d'un jour à
 * l'autre). Jamais de cookie. L'envoi passe par la RPC `track_event_v2`
 * (anon + authenticated), qui reconnaît aussi les robots.
 */

export const TRACK_APPS = ['site', 'app'] as const;
export type TrackApp = (typeof TRACK_APPS)[number];

export const TRACK_PLATFORMS = ['web', 'ios', 'android'] as const;
export type TrackPlatform = (typeof TRACK_PLATFORMS)[number];

export const TRACK_KINDS = [
  'page_view',
  'demo_started',
  'demo_completed',
  'demo_signup_clicked',
] as const;
export type TrackKind = (typeof TRACK_KINDS)[number];

/** Clé `sessionStorage` de l'identifiant de session (un par onglet). */
export const TRACK_SESSION_KEY = 'jr_track_session';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Pages de CONTENU PUBLIC dont l'identifiant est gardé (« * ») : la carte, le
 * pack, l'exercice vus se retrouvent dans les statistiques. Tout le reste
 * (comptes, élèves, jetons de paiement ou de cadeau, défis privés…) garde
 * `:id`. Le nombre de segments doit correspondre exactement.
 */
export const TRACK_KEEP_ROUTES: readonly (readonly string[])[] = [
  // site
  ['skillcards', '*'],
  ['programmes', '*'],
  ['programmes', '*', '*'],
  ['blog', '*'],
  ['objectifs', '*'],
  ['themes', '*'],
  ['challenge', '*'],
  ['live', '*'],
  ['outils', '*'],
  // app
  ['card', '*'],
  ['card', '*', 'acheter'],
  ['c', '*'],
  ['pack', '*', 'acheter'],
  ['debloquer', '*', '*'],
  ['learn', 'exercise', '*'],
  ['demo', 'exercise', '*'],
  ['learn', '*', '*', '*', '*'],
  ['dag', '*'],
];

/** Segment gardé tel quel : uuid ou mot simple (lettres, chiffres, - et _). */
const KEEPABLE = /^[A-Za-z0-9_-]{1,100}$/;

/** Mots qui ne sont jamais un identifiant de contenu (jetons de partage). */
const NEVER_KEPT = new Set(['defi']);

/**
 * Pages dont le segment suivant est un CODE D'ACCÈS (défi privé, paiement,
 * cadeau, connexion, invitation…) : toujours masqué, même court.
 */
export const TRACK_SECRET_PREFIXES: ReadonlySet<string> = new Set([
  'defi-live',
  'payer',
  'cadeau',
  'connexion',
  'rejoindre',
  'lier-parent',
  'invite',
  'offert',
  'join',
]);

function keptSegments(segments: readonly string[]): boolean[] | null {
  for (const route of TRACK_KEEP_ROUTES) {
    if (route.length !== segments.length) continue;
    let ok = true;
    for (let i = 0; i < route.length && ok; i++) {
      const r = route[i]!;
      const seg = segments[i]!;
      ok = r === '*' ? (UUID.test(seg) || KEEPABLE.test(seg)) && !NEVER_KEPT.has(seg) : r === seg;
    }
    if (ok) return route.map((r) => r === '*');
  }
  return null;
}

/**
 * Chemin sans paramètres ni ancre ; uuid, nombres et jetons (≥ 12 caractères
 * avec un chiffre) remplacés par `:id`, sauf l'identifiant d'une page de
 * contenu public (`TRACK_KEEP_ROUTES`) ; 200 caractères au plus.
 */
export function normalizeTrackPath(raw: string | null | undefined): string {
  const path = (raw ?? '/').split('#')[0]!.split('?')[0]!;
  const decoded = path
    .split('/')
    .filter((s) => s !== '')
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s; // segment mal encodé : gardé tel quel
      }
    });
  const keep = keptSegments(decoded);
  const secret = TRACK_SECRET_PREFIXES.has(decoded[0] ?? '');
  const segments = decoded.map((seg, i) => {
    if (secret && i > 0) return ':id';
    if (keep?.[i]) return seg;
    if (UUID.test(seg) || /^\d+$/.test(seg)) return ':id';
    if (seg.length >= 12 && /\d/.test(seg) && /^[A-Za-z0-9_-]+$/.test(seg)) return ':id';
    return seg;
  });
  return `/${segments.join('/')}`.slice(0, 200);
}

/** Identifiant de session aléatoire (24 caractères [A-Za-z0-9]). */
export function newTrackSessionId(random: () => number = Math.random): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 24; i++) id += chars[Math.floor(random() * chars.length)];
  return id;
}

/** Identifiant de l'onglet : relu dans `sessionStorage`, créé au besoin. */
export function trackSessionId(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null | undefined,
): string {
  try {
    const existing = storage?.getItem(TRACK_SESSION_KEY);
    if (existing && /^[A-Za-z0-9_-]{8,40}$/.test(existing)) return existing;
    const id = newTrackSessionId();
    storage?.setItem(TRACK_SESSION_KEY, id);
    return id;
  } catch {
    // Stockage inaccessible (navigation privée stricte) : un identifiant par page.
    return newTrackSessionId();
  }
}

/** Clé `localStorage` du visiteur du jour : « AAAA-MM-JJ:identifiant ». */
export const TRACK_VISITOR_KEY = 'jr_track_visitor';

/** Jour local (AAAA-MM-JJ) d'un instant. */
export function localDayKey(now: Date): string {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

/**
 * Identifiant du visiteur pour la journée en cours : relu s'il date
 * d'aujourd'hui, sinon remplacé. `null` si le stockage est inaccessible (le
 * serveur retombe alors sur l'onglet).
 */
export function trackVisitorId(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!storage) return null;
  try {
    const day = localDayKey(now);
    const raw = storage.getItem(TRACK_VISITOR_KEY) ?? '';
    const [savedDay, id] = raw.split(':');
    if (savedDay === day && id && /^[A-Za-z0-9_-]{8,40}$/.test(id)) return id;
    const next = newTrackSessionId();
    storage.setItem(TRACK_VISITOR_KEY, `${day}:${next}`);
    return next;
  } catch {
    return null;
  }
}

/** Navigateur piloté par un automate (Selenium, Playwright, Puppeteer…). */
export function isAutomatedBrowser(nav: { webdriver?: boolean } | null | undefined): boolean {
  try {
    return nav?.webdriver === true;
  } catch {
    return false;
  }
}

/** Arguments de la RPC `track_event_v2`. */
export function trackEventArgs(
  app: TrackApp,
  platform: TrackPlatform,
  kind: TrackKind,
  path: string,
  session: string,
  visitor: string | null = null,
  automated = false,
) {
  return {
    p_app: app,
    p_platform: platform,
    p_kind: kind,
    p_path: normalizeTrackPath(path),
    p_session: session,
    p_visitor: visitor,
    p_automated: automated,
  };
}

/** Nom de la RPC d'envoi. */
export const TRACK_RPC = 'track_event_v2';

// ── Passage du site à l'app (migration 0718) ────────────────────────────────
// Le site et l'app n'ont pas le même stockage : un lien du site vers l'app
// emporte l'identifiant du visiteur du jour (`?jv=`), que l'app reprend. Les
// tunnels de conversion suivent alors le même visiteur du site jusqu'à
// l'inscription. Seul l'identifiant aléatoire du jour voyage (aucune donnée
// personnelle), et l'app le retire aussitôt de l'adresse.

/** Paramètre d'URL qui porte l'identifiant du visiteur du jour. */
export const TRACK_VISITOR_PARAM = 'jv';

const VISITOR_ID = /^[A-Za-z0-9_-]{8,40}$/;

/** Hôtes de l'app (liens du site à compléter). */
export const APP_HOSTS: ReadonlySet<string> = new Set(['app.jeraisonne.fr']);

/**
 * Lien vers l'app complété de l'identifiant du visiteur ; le lien tel quel s'il
 * ne mène pas à l'app, s'il est illisible ou si l'identifiant est invalide.
 */
export function withVisitorParam(
  href: string,
  visitorId: string | null,
  hosts: ReadonlySet<string> = APP_HOSTS,
): string {
  if (!visitorId || !VISITOR_ID.test(visitorId)) return href;
  try {
    const url = new URL(href);
    if (!hosts.has(url.hostname) || url.searchParams.has(TRACK_VISITOR_PARAM)) return href;
    url.searchParams.set(TRACK_VISITOR_PARAM, visitorId);
    return url.toString();
  } catch {
    return href;
  }
}

/**
 * Reprend l'identifiant du visiteur venu du site : l'enregistre comme visiteur
 * du jour et renvoie l'adresse sans le paramètre (à poser avec
 * `history.replaceState`). `null` si l'adresse ne porte pas d'identifiant.
 */
export function adoptVisitorParam(
  href: string,
  storage: Pick<Storage, 'setItem'> | null | undefined,
  now: Date = new Date(),
): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (!url.searchParams.has(TRACK_VISITOR_PARAM)) return null;
  const id = url.searchParams.get(TRACK_VISITOR_PARAM) ?? '';
  url.searchParams.delete(TRACK_VISITOR_PARAM);
  if (VISITOR_ID.test(id)) {
    try {
      storage?.setItem(TRACK_VISITOR_KEY, `${localDayKey(now)}:${id}`);
    } catch {
      // stockage indisponible : l'app garde son propre visiteur
    }
  }
  return `${url.pathname}${url.search}${url.hash}`;
}
